import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/utils/constants";

const SENTIMENT_CONFIG = {
  bullish: { color: "text-emerald-400", bg: "bg-emerald-500/20", dot: "bg-emerald-400", label: "BULLISH" },
  bearish: { color: "text-rose-400", bg: "bg-rose-500/20", dot: "bg-rose-400", label: "BEARISH" },
  neutral: { color: "text-amber-400", bg: "bg-amber-500/20", dot: "bg-amber-400", label: "NEUTRAL" },
};

const PLATFORM_ICONS = {
  twitter: "X",
  truth_social: "TS",
  news: "NEWS",
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return null; // Return null for items older than 24h
};

// Default items if no ticker data
const DEFAULT_ITEMS = [
  { id: "d1", text: "Markets tracking tariff developments closely — volatility expected around trade policy announcements", source: "Market Watch", sentiment: "neutral", created_at: new Date().toISOString() },
  { id: "d2", text: "Fed officials signal patience on rate cuts, inflation data key for next move", source: "FOMC", sentiment: "bearish", created_at: new Date().toISOString() },
  { id: "d3", text: "ES futures holding above key support — buyers stepping in at 6000 level", source: "Futures Flow", sentiment: "bullish", created_at: new Date().toISOString() },
];

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const MarketTicker = () => {
  const [items, setItems] = useState(DEFAULT_ITEMS);
  const [paused, setPaused] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sources, setSources] = useState([]);

  const fetchAutoFeed = useCallback(async () => {
    try {
      // Try auto-fetch endpoint first
      const res = await axios.get(`${API}/market/feeds/ticker`);
      if (res.data?.items?.length > 0) {
        // Filter to only items < 24h old
        const freshItems = res.data.items.filter(item => {
          const age = timeAgo(item.created_at);
          return age !== null; // null means > 24h
        });
        setItems(freshItems.length > 0 ? freshItems : DEFAULT_ITEMS);
        setLastUpdated(res.data.last_updated);
        setSources(res.data.sources || []);
        return;
      }
    } catch (e) {
      // Auto-feed unavailable, using manual ticker
    }
    
    // Fallback to manual ticker
    try {
      const res = await axios.get(`${API}/market/ticker`);
      if (res.data?.length > 0) {
        // Filter to only items < 24h old
        const freshItems = res.data.filter(item => {
          const age = timeAgo(item.created_at);
          return age !== null;
        });
        if (freshItems.length > 0) {
          setItems(freshItems);
          setLastUpdated(new Date().toISOString());
        }
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchAutoFeed();
    const interval = setInterval(fetchAutoFeed, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchAutoFeed]);

  const displayItems = [...items, ...items];
  return (
    <div
      className="bg-slate-950 border-b border-white/5 overflow-hidden relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="market-ticker"
      data-nosnippet="true"
    >
      {/* Last updated timestamp - Right side */}
      {lastUpdated && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center gap-2 bg-slate-950/90 px-2 py-0.5 rounded">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          <span className="text-[9px] text-slate-500 uppercase tracking-wider">
            Live
          </span>
        </div>
      )}
      
      <div
        className="flex whitespace-nowrap pr-16"
        style={{
          animation: `ticker-scroll ${Math.max(items.length * 4, 30)}s linear infinite`,
          animationPlayState: paused ? "paused" : "running",
        }}
      >
        {displayItems.map((item, i) => {
          const s = SENTIMENT_CONFIG[item.sentiment] || SENTIMENT_CONFIG.neutral;
          const platformIcon = PLATFORM_ICONS[item.platform] || "";
          const itemAge = timeAgo(item.created_at);
          
          // Skip items older than 24h
          if (itemAge === null) return null;
          
          return (
            <a
              key={`${item.id}-${i}`}
              href={item.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-1.5 border-r border-white/10 hover:bg-white/5 transition-colors cursor-pointer"
            >
              {platformIcon && (
                <span className="text-[9px] font-bold text-slate-500 bg-slate-800 px-1 py-0.5 rounded">
                  {platformIcon}
                </span>
              )}
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot} flex-shrink-0`} />
              <span className={`text-[10px] font-bold tracking-wider ${s.color} ${s.bg} px-1.5 py-0.5 rounded`}>
                {s.label}
              </span>
              <span className="text-xs text-slate-100 font-medium max-w-[400px] truncate">{item.text}</span>
              <span className="text-[11px] text-cyan-400 font-semibold">{item.source}</span>
              {itemAge && (
                <span className="text-[10px] text-slate-500">{itemAge}</span>
              )}
            </a>
          );
        })}
      </div>
      <style>{`
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};
